import { FormInput, FormInputColor, FormSelect, FormState, FormTextArea, Labeled } from 'component_utils/FormUtils'
import { cast, itemFilter } from 'component_utils/utils'
import ProcessingButton from 'components/ProcessingButton'
import SimpleModal from 'components/SimpleModal'
import { ISimpleUserDetails, IUserTask, IUserTaskTemplate } from 'interfaces'
import React, { FC, useState } from 'react'
import { FaShareAlt, FaTimes, FaTrash } from 'react-icons/fa'
import { Badge, Button, FormGroup, Input, Label, Table } from 'reactstrap'
import { useApi } from 'utils/API'
import Translation from 'utils/Language/Translation'
import * as immutable from 'object-path-immutable';
import { entries, fromPairs } from 'lodash'
import { GiStrikingArrows } from 'react-icons/gi'
import { useTranslations } from 'utils/language'

const insertParams = (s: string, params: {[k: string]: string}) => {
  let r = s
  for (const [k, v] of entries(params)) {
    r = r.replaceAll(`{${k}}`, v)
  }
  return r
}

const TaskInitializer: FC<{
  trigger: any, 
  users: ISimpleUserDetails[],  
  reloadTasks: any,
  reloadTemplates: any,
}> = ({
  trigger,
  users,
  reloadTasks,
  reloadTemplates,
}) => {
  const [template, setTemplate] = useState<IUserTaskTemplate>({} as any)
  const [state, setState] = useState<any>({})
  const [selectedUsers, setSelectedUsers] = useState<string[]>([])
  const api = useApi()
  const T = useTranslations()

  const parsedParams = {
    ...template.parameters,
    ...fromPairs((
      state?.parameters || []
    ).map((it: any) => [it.name, it.value]))
  } as any

  const toUserTasks = (shared: boolean): IUserTask[] => {
    const base: IUserTask = {
      ...template,
      id: null,
      createdAt: null,
      updatedAt: null,
      finishedAt: null,
      startAt: null,
      version: 0,
      claimedByUser: null,
      timesWorked: [],
      priority: state.priority,
      users: selectedUsers.map(it => parseInt(it)),
      parameters: parsedParams,
      borderColor: state.borderColor,
      title: insertParams(state.title || "", parsedParams),
      text: insertParams(state.text || "", parsedParams),
    }

    if (shared) {
      return [base]
    } else {
      return base.users.map(it => ({
        ...base,
        users: [it]
      }))
    }
  }

  return (
    <SimpleModal
      title={insertParams(state.title || "", parsedParams)}
      fullscreen
      trigger={open => trigger((template: IUserTaskTemplate) => {
        setTemplate(template)
        setState({
          title: template.title,
          text: template.text,
          borderColor: template.borderColor,
          priority: template.priority,
          parameters: entries(template.parameters)
            .map(it => ({ name: it[0], value: it[1] }))
            .filter(it => !it.name.startsWith("_")),
        })
        setSelectedUsers(template.users.map(it => ''+it))
        open()
      })}
      buttons={close => [
        <ProcessingButton color='info' onClick={async () => {
          await Promise.all(toUserTasks(false).map(t => api.userTasks.saveUserTask(t)))
          reloadTasks()
          close()
        }}>
          <GiStrikingArrows/> <Translation name="T.objects.usertasks.createPerUserTask" />
        </ProcessingButton>,
        <ProcessingButton color='primary' onClick={async () => {
          await Promise.all(toUserTasks(true).map(t => api.userTasks.saveUserTask(t)))
          reloadTasks()
          close()
        }}>
          <FaShareAlt/> <Translation name="T.objects.usertasks.createSharedTask" />
        </ProcessingButton>,
      ]}
    >
      <FormState state={state} setState={setState} disableFormWrapper>
        {template?.id === -1 && (
          <>
            <Labeled title={<Translation name="T.misc.title"/>}>
              <FormInput path='title'/>
            </Labeled>
            <Labeled title={<Translation name="T.misc.text"/>}>
              <FormTextArea path='text' rows={5}/>
            </Labeled>
            <Labeled title={<Translation name="T.misc.borderColor"/>}>
              <FormInputColor path='borderColor'/>
            </Labeled>
          </>
        )}

        <Labeled title={<Translation name="T.misc.priority"/>}>
          <FormSelect
            path='priority'
            options={[
              { value: 0, label: T("T.misc.priorities.very-low") },
              { value: 20, label: T("T.misc.priorities.low") },
              { value: 10, label: T("T.misc.priorities.normal") },
              { value: 30, label: T("T.misc.priorities.high") },
              { value: 40, label: T("T.misc.priorities.very-high") },
            ]}
          />
        </Labeled>

        <FormGroup>
          <Label for='user-selector'>User selection</Label>
          <Input id='user-selector' type='select' onChange={e => {
            const value = e.target.value
            setSelectedUsers(old => [...old, value])
            e.target.value = ''
          }}>
            <option value=""></option>
            {users.filter(it => !selectedUsers.includes(''+it.userid)).map(user => {
              return (<option key={user.userid} value={user.userid}>{user.username}</option>)
            })}
          </Input>
        </FormGroup>

        <div className='w-100 mb-5'>
          {selectedUsers.map(userid => {
            const user = users.find(it => (''+it.userid) === userid)
            return (
              <Badge key={userid} pill className='mr-1'>
                {user.username}
                <Button className='p-0' onClick={() => setSelectedUsers(old => old.filter(itemFilter([userid])))}>
                  <FaTimes/>
                </Button>
              </Badge>
            )
          })}
        </div>

        {(state?.parameters ?? []).length > 0 && (
          <Table striped>
            <thead>
              <tr>
                <th className='w-50'><Translation name="T.misc.name"/></th>
                <th className='w-50'><Translation name="T.misc.value"/></th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {cast<any[]>(state?.parameters || []).map((it: any, idx) => (
                <tr key={idx}>
                  <td>
                    <FormInput path={`parameters.${idx}.name`}/>
                  </td>
                  <td>
                    <FormInput path={`parameters.${idx}.value`}/>
                  </td>
                  <td>
                    <Button color='danger' onClick={() => {
                      setState((old: any) => immutable.del(old, 'parameters.'+idx))
                    }}>
                      <FaTrash/>
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </FormState>
    </SimpleModal>
  )
}

export default TaskInitializer